/* eslint-disable no-nested-ternary */

import React, { useCallback, useContext, useMemo } from "react";
import Header from "../Header";
import cn from "clsx";
import { LevelScreenProps } from "../../types/interfaces";
import SwitchIcon from "../../Icons/SwitchIcon";
import { LanguageContext } from "../../utils/context/languageContext";
import { LanguageKeys } from "../../utils/languageKeys";
import { useConfig } from "../../utils/context/configContext";

function LevelScreen({ angle, countdown, isScanning, isInTargetRange, stabilityScore, children }: Omit<LevelScreenProps, "config">) {
	const { translate } = useContext(LanguageContext) || {};
	const config = useConfig();

	const getProximityToTarget = useCallback(() => {
		if (angle < 80) return Math.max(0, Math.min(100, (angle - 60) * 10));
		if (angle > 95) return Math.max(0, Math.min(100, (105 - angle) * 10));
		return 100;
	}, [angle]);

	const backgroundColor = useMemo(() => {
		if (isScanning) return config?.style?.angleDetector?.successAngleBackground || "#4f46e5";

		const proximity = getProximityToTarget();
		if (proximity === 0) return config?.style?.angleDetector?.successAngleLowBackground || "#ffffff";
		if (proximity === 100) return config?.style?.angleDetector?.successAngleBackground || "#4f46e5";

    const r = Math.round(255 - (255 - 139) * (proximity / 100));
    const g = Math.round(255 - (255 - 92) * (proximity / 100));
    const b = Math.round(255 - (255 - 246) * (proximity / 100));
    return `rgb(${r}, ${g}, ${b})`;
  }, [isScanning, getProximityToTarget]);

	const handelTextColour = useCallback((isLight: boolean, proximity: number) => {
		if (proximity > 70) {
			return isLight ? `text-[${config?.style?.angleDetector?.successAngleTextLightColor}]/70` : `text-[${config?.style?.angleDetector?.successAngleTextLightColor}]`;
		}
		return isLight ? `text-[${config?.style?.angleDetector?.successAngleTextLightColor}]/40` : `text-[${config?.style?.angleDetector?.successAngleTextDarkColor}]/70`;
	}, []);

	const getTextColor = useCallback(
		(isLight = false) => {
			const proximity = getProximityToTarget();
			if (isScanning) return `text-[${config?.style?.angleDetector?.successAngleTextLightColor}]`;
			return handelTextColour(isLight, proximity);
		},
		[isScanning, getProximityToTarget],
	);

	const handelColourCode = useCallback((isLight: boolean, proximity: number) => {
		if (proximity > 70) {
			return isLight ? config?.style?.angleDetector?.successAngleTextLightColor : config?.style?.angleDetector?.successAngleTextLightColor;
		}
		return isLight ? config?.style?.angleDetector?.successAngleTextLightColor : config?.style?.angleDetector?.successAngleTextDarkColor;
	}, []);
	const getColorCode = useCallback(
		(isLight = false) => {
			const proximity = getProximityToTarget();
			if (isScanning) return config?.style?.angleDetector?.successAngleTextLightColor;
			return handelColourCode(isLight, proximity);
		},
		[isScanning, getProximityToTarget],
	);
	const cd = countdown;
	function fixVh() {
		document.documentElement.style.setProperty("--real-vh", window.innerHeight + "px");
	}
	fixVh();
	window.addEventListener("resize", fixVh);
	return (
		<div
			className="flex common-ui-main  w-screen flex-col items-center h-[var(--real-vh)] overflow-hidden touch-none justify-center transition-all duration-300 max-w-[28rem] mx-auto"
			style={{ backgroundColor }}
			data-testid="level-screen"
			data-angle-state={isScanning ? "locked" : isInTargetRange ? "target" : "adjusting"}
		>
			<div className="flex justify-start fixed top-[.5rem] max-w-[28rem] mx-auto w-full px-[1rem]">
				<div className="flex justify-start   ">
					<Header noTitle />
				</div>
			</div>
			{cd !== null ? (
				<div
					className="relative flex h-[6rem] w-[6rem] items-center justify-center rounded-[9999px]  bg-[#fff]/20 transition-all duration-300"
					style={{
						border: `2px solid ${getColorCode()}`,
					}}
				>
					<div
						className={`text-[3rem] font-bold text-[${getColorCode()}]`}
						style={{
							fontFamily: config?.style?.base?.baseFontFamily || "Inter, sans-serif",
                            color: getColorCode(),
						}}
					>
						{cd}
					</div>
				</div>
			) : isScanning ? (
				<div className="relative flex flex-col items-center justify-center">
					<div className="relative flex h-16 w-16 items-center justify-center rounded-[9999px] border-2 bg-[#fff]/30 border-[#fff]">
						<div className="h-4 w-4 rounded-[9999px]  animate-pulse bg-[#fff]/80" />
					</div>
					{children}
				</div>
			) : (
				<div
					className={cn("relative flex h-[4rem] w-[4rem] items-center justify-center rounded-[9999px]  ", isInTargetRange ? "bg-[#fff]/20 border-[#fff]" : "bg-[#fff]/10 border-[#fff]")}
					style={{
						transform: `translateY(${(90 - angle) * 3}px)`,
						transition: "transform 0.2s ease-out",
						border: `2px solid ${getColorCode()}`,
					}}
				>
					<div className={`h-[1rem] w-[1rem] rounded-[9999px] bg-[${getColorCode()}]/80`}
                    style={{
						backgroundColor: `${getColorCode()}B3`,
					}}
                    />
					<div
						// className=" text-[#fff]"
						className={cn("mt-[.5rem] text-center w-[180px] flex-col flex items-center  absolute top-[60px]", getTextColor())}
                         style={{
						color: getColorCode(),
					}}
					>
						<SwitchIcon size={30} />
						<p
							style={{
								fontFamily: config?.style?.base?.baseFontFamily || "Inter, sans-serif",
							}}
						>
							{" "}
							{translate?.(LanguageKeys.startLevelCheck)}
							<br />
						</p>
					</div>
				</div>
			)}

			{cd !== null && (
				<div className="absolute bottom-[8rem] text-center">
					<div
						className={cn("text-sm font-medium px-4 py-1.5 bg-black/20 rounded-[9999px] mt-8", getTextColor())}
						style={{
							fontFamily: config?.style?.base?.baseFontFamily || "Inter, sans-serif",
                            color: getColorCode(),
						}}
					>
						{translate?.(LanguageKeys.leavePhone)}
					</div>
				</div>
			)}
			{isInTargetRange && cd === null && !isScanning && (
				<div className="absolute bottom-[8rem] w-[12rem]">
					<div
						className={`h-[.375rem] w-full bg-[${getColorCode()}]/20 rounded-[9999px] overflow-hidden`}
						style={{
							backgroundColor: `${getColorCode()}33`, // 20% opacity
						}}
					>
						<div
							className={`h-full bg-[${getColorCode()}]/70 transition-all duration-300 rounded-[9999px]`}
							style={{
								width: `${angle}%`,
								backgroundColor: `${getColorCode()}B3`,
							}}
						/>
					</div>
				</div>
			)}

      {cd === null && !isScanning && (
        <div className="absolute bottom-[5rem] text-center">
          <div className={cn("text-[1.5rem] font-light", getTextColor())}
           style={{
						color: getColorCode(),
					}}
          >
            {Math.round(angle)}°
          </div>
        </div>
      )}

			{cd === null && !isScanning && (
				<div className="absolute bottom-[2rem] text-center max-w-[20rem] mx-auto">
					<div
						className={cn("text-[.75rem] opacity-50", getTextColor())}
						style={{
							fontFamily: config?.style?.base?.baseFontFamily || "Inter, sans-serif",
                            color: getColorCode(),
						}}
					>
						{translate?.(LanguageKeys.placePhoneUpright)}
					</div>
				</div>
			)}
		</div>
	);
}
export default React.memo(LevelScreen);
